class WaveAnimation {
    constructor() {
        this.canvas = document.getElementById("waveCanvas");
        this.ctx = this.canvas.getContext("2d");
        this.animationId = null;
        this.time = 0;

        // Fixed default settings (no controls)
        this.maxHeight = 0.5;
        this.waveAggression = 0.15;
        this.waveFrequency = 1.0;
        this.animationSpeed = 0.015;

        // Internal technical parameters
        this.gridSpacing = 20;
        this.dotSize = 1.8;
        this.perspective = 0.6;
        this.baseWaveAmplitude = 20;

        // Color gradient for depth
        this.colors = {
            deep: [0, 100, 180],
            mid: [0, 150, 220],
            surface: [0, 200, 255],
            highlight: [100, 230, 255],
            foam: [255, 255, 255],
        };

        // Foam effect parameters
        this.foamThreshold = 0.3;
        this.foamIntensity = 1.0;

        // Mouse interaction parameters
        this.mouseX = -1000;
        this.mouseY = -1000;
        this.mouseRadius = 150;
        this.maxRepulsion = 35;
        this.dotOffsets = new Map();
        this.mouseAnimationSpeed = 0.08;

        this.init();
    }

    init() {
        this.resizeCanvas();
        this.bindEvents();
        this.animate();
    }

    resizeCanvas() {
        const rect = this.canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;

        this.canvas.width = rect.width * dpr;
        this.canvas.height = rect.height * dpr;

        this.ctx.scale(dpr, dpr);

        this.canvasWidth = rect.width;
        this.canvasHeight = rect.height;
    }

    bindEvents() {
        window.addEventListener("resize", () => {
            this.resizeCanvas();
        });

        document.addEventListener("visibilitychange", () => {
            if (document.hidden) {
                this.pauseAnimation();
            } else {
                this.resumeAnimation();
            }
        });

        // Mouse tracking for cursor repulsion
        this.canvas.addEventListener("mousemove", (e) => {
            const rect = this.canvas.getBoundingClientRect();
            this.mouseX = e.clientX - rect.left;
            this.mouseY = e.clientY - rect.top;
        });

        this.canvas.addEventListener("mouseleave", () => {
            this.mouseX = -1000;
            this.mouseY = -1000;
        });
    }

    calculateSmoothMouseRepulsion(originalX, originalY, pointId) {
        const dx = originalX - this.mouseX;
        const dy = originalY - this.mouseY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        let targetOffsetX = 0;
        let targetOffsetY = 0;

        if (distance <= this.mouseRadius && distance > 0) {
            const repulsionStrength =
                (this.mouseRadius - distance) / this.mouseRadius;
            const repulsionDistance = repulsionStrength * this.maxRepulsion;

            const directionX = dx / distance;
            const directionY = dy / distance;

            targetOffsetX = directionX * repulsionDistance;
            targetOffsetY = directionY * repulsionDistance;
        }

        const currentOffset = this.dotOffsets.get(pointId) || { x: 0, y: 0 };

        const deltaX = targetOffsetX - currentOffset.x;
        const deltaY = targetOffsetY - currentOffset.y;

        const ease = 1 - Math.pow(1 - this.mouseAnimationSpeed, 3);

        const newOffsetX = currentOffset.x + deltaX * ease;
        const newOffsetY = currentOffset.y + deltaY * ease;

        this.dotOffsets.set(pointId, { x: newOffsetX, y: newOffsetY });

        return { x: newOffsetX, y: newOffsetY };
    }

    calculateWaveHeight(x, z, time) {
        const baseAmplitude = this.baseWaveAmplitude;
        const speed = this.animationSpeed;

        const amplitude = baseAmplitude;

        const layer1 = this.calculateWaveLayer(
            x,
            z,
            time,
            speed,
            amplitude,
            1.0,
            0.0
        );
        const layer2 = this.calculateWaveLayer(
            x,
            z,
            time,
            speed,
            amplitude * 0.7,
            1.3,
            0.5
        );
        const layer3 = this.calculateWaveLayer(
            x,
            z,
            time,
            speed,
            amplitude * 0.5,
            1.7,
            1.2
        );
        const layer4 = this.calculateWaveLayer(
            x,
            z,
            time,
            speed,
            amplitude * 0.3,
            2.2,
            2.1
        );

        return layer1 + layer2 + layer3 + layer4;
    }

    calculateWaveLayer(
        x,
        z,
        time,
        speed,
        amplitude,
        speedMultiplier,
        phaseOffset
    ) {
        const layerTime = time * speedMultiplier + phaseOffset;

        const waveIntensity = Math.pow(this.waveAggression, 0.6);

        const baseRolling =
            Math.sin(z * 0.006 + layerTime * speed) * amplitude * 0.5;
        const crossoverRoll =
            Math.sin(z * 0.012 + layerTime * speed * 1.3) * amplitude * 0.4;

        const rowPhaseOffset = z * 0.003 * this.waveFrequency;

        const horizontalWave1 =
            Math.sin(x * 0.012 + rowPhaseOffset + layerTime * speed * 0.8) *
            amplitude *
            0.4 *
            waveIntensity;
        const horizontalWave2 =
            Math.sin(
                x * 0.008 + rowPhaseOffset * 1.3 + layerTime * speed * 1.2
            ) *
            amplitude *
            0.3 *
            waveIntensity;
        const horizontalWave3 =
            Math.sin(
                x * 0.02 + rowPhaseOffset * 0.7 + layerTime * speed * 0.6
            ) *
            amplitude *
            0.2 *
            waveIntensity;

        const irregularWave1 =
            Math.sin(
                x * 0.007 +
                    rowPhaseOffset * 1.8 +
                    z * 0.004 +
                    layerTime * speed * 1.8
            ) *
            amplitude *
            0.25 *
            waveIntensity;
        const irregularWave2 =
            Math.sin(
                x * 0.025 +
                    rowPhaseOffset * 0.5 +
                    z * 0.0012 +
                    layerTime * speed * 2.5
            ) *
            amplitude *
            0.15 *
            waveIntensity;

        const rowPhase = Math.sin(z * 0.008) * 1.2;
        const crossoverWave =
            Math.sin(
                x * 0.018 +
                    rowPhaseOffset * 1.1 +
                    rowPhase +
                    layerTime * speed * 1.1
            ) *
            amplitude *
            0.35 *
            waveIntensity;

        const intersectionWave =
            Math.sin(z * 0.015 + x * 0.005 + layerTime * speed * 0.7) *
            amplitude *
            0.3;

        return (
            baseRolling +
            crossoverRoll +
            horizontalWave1 +
            horizontalWave2 +
            horizontalWave3 +
            irregularWave1 +
            irregularWave2 +
            crossoverWave +
            intersectionWave
        );
    }

    project3D(x, y, z) {
        const perspective = 1 / (1 + z * this.perspective * 0.001);
        const screenX = x * perspective + this.canvasWidth * 0.5;

        const baseSeaLevel = this.canvasHeight * 0.7;
        const topLimit = this.canvasHeight * 0.4;

        const depthVerticalScale = 1.0;

        const maxAllowedWaveHeight = baseSeaLevel - topLimit;
        const scaleFactor = Math.min(
            1.0,
            maxAllowedWaveHeight /
                (this.baseWaveAmplitude * this.waveAggression * 2.5)
        );

        let scaledY =
            y * perspective * depthVerticalScale * scaleFactor +
            z * this.perspective * 0.8;
        const screenY = baseSeaLevel + scaledY;

        const maxAmplitude = this.baseWaveAmplitude * this.waveAggression * 3.0;
        const normalizedHeight = Math.max(-1, Math.min(1, y / maxAmplitude));

        const waveScale = 0.4 + (normalizedHeight * 0.3 + 0.5) * 1.2;
        const finalScale = perspective * waveScale;

        return { x: screenX, y: screenY, depth: z, scale: finalScale };
    }

    getColorForPoint(height, depth, scale) {
        const amplitude = this.baseWaveAmplitude * this.waveAggression * 3.0;
        const normalizedHeight = Math.max(-1, Math.min(1, height / amplitude));

        const depthFactor = Math.max(0, Math.min(1, depth / 500));

        const timeCycle = this.time * this.animationSpeed * 0.3;
        const colorCycle =
            (depthFactor * Math.PI * 3 + timeCycle) % (Math.PI * 2);
        const colorPosition = (Math.sin(colorCycle) + 1) / 2;

        let baseColor;
        if (colorPosition < 0.25) {
            baseColor = this.interpolateColor(
                this.colors.deep,
                this.colors.mid,
                colorPosition * 4
            );
        } else if (colorPosition < 0.5) {
            baseColor = this.interpolateColor(
                this.colors.mid,
                this.colors.surface,
                (colorPosition - 0.25) * 4
            );
        } else if (colorPosition < 0.75) {
            baseColor = this.interpolateColor(
                this.colors.surface,
                this.colors.highlight,
                (colorPosition - 0.5) * 4
            );
        } else {
            baseColor = this.interpolateColor(
                this.colors.highlight,
                this.colors.deep,
                (colorPosition - 0.75) * 4
            );
        }

        const waveBrightness = 0.5 + (normalizedHeight * 0.3 + 0.5) * 0.5;

        const foamFactor =
            Math.max(0, normalizedHeight - this.foamThreshold) /
            (1 - this.foamThreshold);
        const foamNoise = (Math.sin(this.time * 0.1 + height * 0.01) + 1) / 2;
        const shouldShowFoam =
            foamFactor > 0 && foamNoise < foamFactor * this.foamIntensity;

        let finalColor;
        if (shouldShowFoam) {
            const foamMix = Math.min(0.95, foamFactor + 0.3);
            finalColor = [
                Math.round(
                    baseColor[0] * (1 - foamMix) + this.colors.foam[0] * foamMix
                ),
                Math.round(
                    baseColor[1] * (1 - foamMix) + this.colors.foam[1] * foamMix
                ),
                Math.round(
                    baseColor[2] * (1 - foamMix) + this.colors.foam[2] * foamMix
                ),
            ];
        } else {
            finalColor = [
                Math.round(baseColor[0] * waveBrightness),
                Math.round(baseColor[1] * waveBrightness),
                Math.round(baseColor[2] * waveBrightness),
            ];
        }

        const opacity = Math.max(
            0.6,
            Math.min(1.0, scale * (1.2 - depthFactor * 0.3))
        );

        return `rgba(${finalColor[0]}, ${finalColor[1]}, ${finalColor[2]}, ${opacity})`;
    }

    interpolateColor(color1, color2, factor) {
        return [
            Math.round(color1[0] + (color2[0] - color1[0]) * factor),
            Math.round(color1[1] + (color2[1] - color1[1]) * factor),
            Math.round(color1[2] + (color2[2] - color1[2]) * factor),
        ];
    }

    getRowColor(rowIndex) {
        const totalRows = Math.floor(this.canvasHeight / this.gridSpacing);
        const rowRatio = rowIndex / totalRows;

        if (rowRatio < 0.3) {
            return this.interpolateColor(
                [0, 100, 150],
                [0, 150, 200],
                rowRatio / 0.3
            );
        } else if (rowRatio < 0.7) {
            return this.interpolateColor(
                [0, 150, 200],
                [0, 180, 255],
                (rowRatio - 0.3) / 0.4
            );
        } else {
            return this.interpolateColor(
                [0, 180, 255],
                [100, 200, 255],
                (rowRatio - 0.7) / 0.3
            );
        }
    }

    getRowSize(rowIndex) {
        const totalRows = Math.floor(this.canvasHeight / this.gridSpacing);
        const rowRatio = rowIndex / totalRows;

        if (rowRatio < 0.5) {
            return 1 + rowRatio * 2;
        } else {
            return 2 + (rowRatio - 0.5) * 2;
        }
    }

    draw3DSurface() {
        const points = [];

        const extraWidth = this.canvasWidth * 0.8;
        const gridSpacingX = this.gridSpacing * 0.8;
        const gridStartX = -extraWidth;
        const gridEndX = this.canvasWidth + extraWidth;
        const gridStepsX = Math.ceil((gridEndX - gridStartX) / gridSpacingX);

        const gridStartZ = -200;
        const gridEndZ = 1000;
        const gridStepsZ = Math.ceil(
            (gridEndZ - gridStartZ) / this.gridSpacing
        );

        for (let rowIndex = 0; rowIndex < gridStepsZ; rowIndex++) {
            const worldZ = gridStartZ + rowIndex * this.gridSpacing;

            const rowOffset = (rowIndex % 2) * gridSpacingX * 0.5;
            const rowVariation =
                Math.sin(rowIndex * 0.2 + this.time * 0.008) *
                this.gridSpacing *
                0.15;

            for (let colIndex = 0; colIndex < gridStepsX; colIndex++) {
                const baseWorldX =
                    gridStartX +
                    colIndex * gridSpacingX +
                    rowOffset +
                    rowVariation;

                const colStagger =
                    Math.sin(colIndex * 0.15 + rowIndex * 0.08) *
                    this.gridSpacing *
                    0.1;
                const finalWorldX = baseWorldX + colStagger;

                const worldY = this.calculateWaveHeight(
                    finalWorldX,
                    worldZ,
                    this.time
                );
                const projected = this.project3D(finalWorldX, worldY, worldZ);

                if (
                    projected.x < -100 ||
                    projected.x > this.canvasWidth + 100 ||
                    projected.y < -300
                )
                    continue;

                points.push({
                    x: projected.x,
                    y: projected.y,
                    height: worldY,
                    depth: worldZ,
                    scale: projected.scale,
                    actualY: projected.y,
                    rowIndex: rowIndex,
                });
            }
        }

        points.sort((a, b) => {
            const yDiff = a.actualY - b.actualY;
            if (Math.abs(yDiff) > 2) return yDiff;
            return b.depth - a.depth;
        });

        points.forEach((point, index) => {
            const pointId = `${Math.round(point.x / 10)}_${Math.round(
                point.y / 10
            )}`;

            const repulsion = this.calculateSmoothMouseRepulsion(
                point.x,
                point.y,
                pointId
            );

            const finalX = point.x + repulsion.x;
            const finalY = point.y + repulsion.y;

            const color = this.getColorForPoint(
                point.height,
                point.depth,
                point.scale
            );
            const size = this.dotSize * point.scale;

            if (size > 0.1) {
                this.ctx.fillStyle = color;
                this.ctx.beginPath();
                this.ctx.arc(finalX, finalY, size, 0, Math.PI * 2);
                this.ctx.fill();

                if (point.scale > 0.8 && this.waveAggression > 0.8) {
                    this.ctx.shadowBlur = size;
                    this.ctx.shadowColor = color;
                    this.ctx.fill();
                    this.ctx.shadowBlur = 0;
                }
            }
        });
    }

    render() {
        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.draw3DSurface();
    }

    animate() {
        this.time += 1;
        this.render();
        this.animationId = requestAnimationFrame(() => this.animate());
    }

    pauseAnimation() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }

    resumeAnimation() {
        if (!this.animationId) {
            this.animate();
        }
    }

    destroy() {
        this.pauseAnimation();
        window.removeEventListener("resize", this.resizeCanvas);
        document.removeEventListener("visibilitychange", this.pauseAnimation);
    }
}

document.addEventListener("DOMContentLoaded", () => {
    const waveAnimation = new WaveAnimation();

    window.addEventListener("beforeunload", () => {
        waveAnimation.destroy();
    });
});

window.addEventListener("load", () => {
    const canvas = document.getElementById("waveCanvas");
    const ctx = canvas.getContext("2d");

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
});
